import { getSupabaseBrowserClient } from '../lib/supabase'

export type TechnologyFamilyResolutionState = 'resolved' | 'missing_classification' | 'unavailable_source'
export type TechnologyFamilySourceKind = 'product_catalogue' | 'legacy_template' | 'confirmed_solution'

export type TechnologyFamilyResolution = {
  state: TechnologyFamilyResolutionState
  selectedProductId: string | null
  selectedSolutionId: string | null
  reason?: string
  familyCode?: string
  classificationSetId?: string
  classificationSetVersion?: number
  sourceKind?: TechnologyFamilySourceKind
  versionRef?: Record<string, string>
  sourceSnapshot?: Record<string, unknown>
}

type RpcError = { message: string }
type RpcResponse = PromiseLike<{ data: unknown; error: RpcError | null }>
type RpcInvoker = (functionName: string, args?: Record<string, unknown>) => RpcResponse

function asObject(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {}
}

function stringOrNull(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null
}

function numberOrUndefined(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : undefined
}

export async function resolveAiUseTechnologyFamily(input: {
  draftId: string
  productId?: string | null
  solutionId?: string | null
  legacyTemplateVersionId?: string | null
  savedResolution?: TechnologyFamilyResolution | null
}): Promise<TechnologyFamilyResolution> {
  const client = getSupabaseBrowserClient() as unknown as { rpc: RpcInvoker }
  const args: Record<string, unknown> = {
    p_draft_id: input.draftId,
    p_product_id: input.productId || null,
    p_solution_id: input.solutionId || null,
    p_legacy_template_version_id: input.legacyTemplateVersionId || null,
  }
  if (input.savedResolution) args.p_saved_resolution = input.savedResolution

  const { data, error } = await client.rpc('resolve_ai_use_technology_family', args)
  if (error) throw new Error(error.message)

  const row = asObject(data)
  const state = row.state
  if (state !== 'resolved' && state !== 'missing_classification' && state !== 'unavailable_source') {
    throw new Error('Technology Family lookup returned an invalid state')
  }

  const versionRef = asObject(row.versionRef)
  const sourceSnapshot = asObject(row.sourceSnapshot)
  return {
    state,
    selectedProductId: stringOrNull(row.selectedProductId),
    selectedSolutionId: stringOrNull(row.selectedSolutionId),
    reason: stringOrNull(row.reason) ?? undefined,
    familyCode: stringOrNull(row.familyCode) ?? undefined,
    classificationSetId: stringOrNull(row.classificationSetId) ?? undefined,
    classificationSetVersion: numberOrUndefined(row.classificationSetVersion),
    sourceKind: row.sourceKind === 'product_catalogue' || row.sourceKind === 'legacy_template' || row.sourceKind === 'confirmed_solution'
      ? row.sourceKind
      : undefined,
    versionRef: Object.keys(versionRef).length > 0
      ? Object.fromEntries(Object.entries(versionRef).filter((entry): entry is [string, string] => typeof entry[1] === 'string'))
      : undefined,
    sourceSnapshot: Object.keys(sourceSnapshot).length > 0 ? sourceSnapshot : undefined,
  }
}
